import {Animated, Pressable, View} from "react-native"
import React, {memo, useMemo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component"
import {digg} from "diggerize"
import HayaDatePicker from "haya-date-picker/build/date-picker"
import Icon from "@kaspernj/api-maker/build/utils/icon"
import mergeStyles from "@kaspernj/api-maker/build/merge-styles"
import {Portal} from "conjointment"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import Text from "@kaspernj/api-maker/build/utils/text"
import useDates from "components/utils/use-dates"
import useI18n from "i18n-on-steroids/src/use-i18n.mjs"
import usePressOutside from "outside-eye/build/use-press-outside"

export default memo(shapeComponent(class ComponentsChalkyLayoutDatePicker extends ShapeComponent {
  datePickerPositionViewLeft = new Animated.Value(0)
  datePickerPositionViewTop = new Animated.Value(0)

  static defaultProps = {
    style: null
  }

  static propTypes = propTypesExact({
    dateFrom: PropTypes.instanceOf(Date),
    dateTo: PropTypes.instanceOf(Date),
    onSelect: PropTypes.func.isRequired,
    style: PropTypes.object,
    testID: PropTypes.string
  })

  setup() {
    const {l} = useDates()
    const {t} = useI18n({namespace: "js.components.chalky_layout.date_picker"})

    this.setInstance({
      datePickerViewRef: React.useRef(),
      l,
      t
    })
    this.useStates({
      showDatePicker: false
    })

    usePressOutside(this.tt.datePickerViewRef, this.tt.onClickOutsideDatePicker)
  }

  render() {
    const {datePickerPositionViewLeft, datePickerPositionViewTop, l, t} = this.tt
    const {dateFrom, dateTo, style} = this.p
    const {showDatePicker} = this.s

    const actualStyle = useMemo(() => mergeStyles([style, {
      flexDirection: "row",
      alignItems: "center"
    }]), [style])

    return (
      <View>
        <Pressable onPress={this.tt.onPress} style={actualStyle} testID={this.props.testID}>
          <View
            style={this.contentViewStyle ||= {
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "#f0f4fc",
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8,
              padding: 12
            }}
          >
            <View style={this.calendarIconViewStyle ||= {marginRight: 10}}>
              <Icon color="#243494" name="calendar" />
            </View>
            {dateFrom &&
              <Text style={this.dateFromTextStyle ||= {color: "#6783b8"}}>
                {l(dateFrom, "date")}
              </Text>
            }
            {dateFrom && dateTo &&
              <Text style={this.spaceTextStyle ||= {color: "#6783b8"}}> - </Text>
            }
            {dateTo &&
              <Text style={this.dateToTextStyle ||= {color: "#6783b8"}}>
                {l(dateTo, "date")}
              </Text>
            }
            {!dateFrom && !dateTo &&
              <Text style={this.nothingSelectTextStyle ||= {color: "#6783b8"}}>
                {t(".nothing_selected")}
              </Text>
            }
          </View>
          <View
            style={this.chevronDownViewStyle ||= {
              alignSelf: "stretch",
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "#e1e9f7",
              borderTopRightRadius: 8,
              borderBottomRightRadius: 8,
              padding: 12
            }}
          >
            <Icon name="chevron-down" />
          </View>
        </Pressable>
        <View onLayout={this.tt.onDatePickerPositionViewLayout} />
        {showDatePicker &&
          <Portal name="date-picker">
            <Animated.View
              ref={this.tt.datePickerViewRef}
              style={this.datePickerViewStyle ||= {
                position: "absolute",
                left: datePickerPositionViewLeft,
                top: datePickerPositionViewTop
              }}
              testID="date-picker-dialog"
            >
              <HayaDatePicker mode="dateRange" onSelect={this.tt.onDateSelect} />
            </Animated.View>
          </Portal>
        }
      </View>
    )
  }

  onClickOutsideDatePicker = () => this.setState({showDatePicker: false})

  onDateSelect = (...args) => {
    this.p.onSelect(...args)
    this.setState({showDatePicker: false})
  }

  onDatePickerPositionViewLayout = (e) => {
    this.datePickerPositionViewTop.setValue(digg(e, "nativeEvent", "layout", "top"))
    this.datePickerPositionViewLeft.setValue(digg(e, "nativeEvent", "layout", "left"))
  }

  onPress = () => this.setState({showDatePicker: !this.s.showDatePicker})
}))
